import { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { nanoid } from '@reduxjs/toolkit'
import { noop } from 'lodash'
import Clipboard from 'clipboard'
import classnames from 'classnames'
import Input from './input'
import Button from '../Button'

export default class CopyableInput extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      copiedToClipboard: false,
      copyError: false,
      lastCopiedValue: null,
    }
    this.contextIdentifier = nanoid()
    this.clipboard = null
  }

  copyButtonClassName() {
    return `js-copy-button-${this.contextIdentifier}`
  }

  targetButtonClassName() {
    return `js-clipboard-target-${this.contextIdentifier}`
  }

  componentDidMount() {
    this.clipboard = new Clipboard(`.${this.copyButtonClassName()}`)
    this.clipboard.on('success', this.handleClipboardCopySuccess)
    this.clipboard.on('error', this.handleClipboardCopyError)
  }

  componentWillUnmount() {
    if (this.clipboard) this.clipboard.destroy()
  }

  static getDerivedStateFromProps(props, state) {
    const { value } = props
    const { lastCopiedValue } = state
    if (lastCopiedValue !== value) {
      return {
        copiedToClipboard: false,
        copyError: false,
      }
    }
    return null
  }

  handleCopyClick = event => {
    event.preventDefault()
  }

  handleClipboardCopySuccess = event => {
    const { text } = event
    this.setState({
      copiedToClipboard: true,
      copyEror: false,
      lastCopiedValue: text,
    })
    event.clearSelection()
  }

  handleClipboardCopyError = error => {
    this.setState({
      copiedToClipboard: false,
      copyError: true,
    })
  }

  getCopyStatusMessage() {
    const { copiedToClipboard, copyError } = this.state
    if (copyError) {
      return 'Copy to clipboard failed, please copy manually'
    } else if (copiedToClipboard) {
      return 'Copied to clipboard!'
    }
    return null
  }

  render() {
    const { className, inputClassName, buttonClassName } = this.props
    const { copiedToClipboard, copyError } = this.state
    const message = this.getCopyStatusMessage()
    const id = `copyable-input-${this.contextIdentifier}`
    const buttonClasses = classnames(
      buttonClassName,
      this.copyButtonClassName(),
      'copyable-input__button',
      { 'hint hint--medium  hint--timetoast hint--top-left': copiedToClipboard || copyError },
      { 'hint--timetoast-error': copyError },
      { 'hint--timetoast-success': copiedToClipboard }
    )

    const inputPropsToTransfer = Object.assign({}, this.props, {
      id,
      className: classnames(this.props.inputClassName, 'copyable-input__input', `${this.targetButtonClassName()}`),
    })
    return (
      <div className={classnames(className, 'copyable-input')}>
        <Input {...inputPropsToTransfer} />
        <Button
          className={buttonClasses}
          aria-label={message}
          data-clipboard-target={`.${this.targetButtonClassName()}`}
          onClick={this.handleCopyClick}
        >
          Copy
        </Button>
      </div>
    )
  }
}
CopyableInput.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  spellCheck: PropTypes.bool,
  type: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  buttonClassName: PropTypes.string,
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
}
CopyableInput.defaultProps = {
  id: null,
  value: null,
  spellCheck: null,
  type: 'text',
  step: null,
  className: null,
  inputClassName: 'input',
  buttonClassName: null,
  checked: null,
  onChange: noop,
  onBlur: noop,
  autoFocus: false,
  placeholder: null,
}
